import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { emptyClassesObj } from '@app/account/profile/user-profile.variables';
import { findSubjectById } from '@app/core/services/bff/helpers.service';
import { UserServiceBff } from '@app/core/services/bff/user-profile.service';
import { ContentBlock } from '@app/core/services/interfaces';
import { AccountService, UserServicesBffService } from '@core/services';
import { BffGetUserServicesEndpoint, FindUserServicesFilter, UserService } from 'lingo2-api-models';
import {
  CurrencyEnum,
  EContentPanelRoute,
  ITeachingSubject,
  MeetingTypeEnum,
  SubjectCategoryEnum,
  UserSegmentEnum,
  UserServiceCategory,
  UserStatusEnum,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
  total?: number;
}

@Component({
  selector: 'app-user-profile-classes-subpage',
  templateUrl: './user-profile-classes-subpage.component.html',
  styleUrls: ['./user-profile-classes-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileClassesSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit, OnDestroy {
  userServicesV2Service = inject(UserServicesBffService);
  private readonly PAGE_SIZE = 10;
  private isDestroyed = false;

  @Input()
  public set selectedSubjectId(selectedSubjectId: string) {
    this._selectedSubjectId = selectedSubjectId;
    this.init();
  }

  public get selectedSubjectId(): string {
    return this._selectedSubjectId;
  }

  @Output() public onChangeSubject = new EventEmitter<string>();
  @Output() public isCloseAllModals = new EventEmitter<boolean>();
  @Output() public loaded = new EventEmitter<boolean>();
  public teachingSubjectsList: ITeachingSubjectItem[] = [];

  public services: ContentBlock<UserService, FindUserServicesFilter> = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
    },
  };

  public emptyClassesObj = emptyClassesObj;
  public editService: UserServiceBff;
  public activeServiceIndex = -1;
  public checkoutService: UserService;
  public isMediaPrepared = false;
  public teachingSubject: ITeachingSubject;
  public safeMediaUrl: SafeResourceUrl;
  public userServiceCategoryTabs: UserServiceCategory[] = [
    { id: -1, code: 'all', title: 'user-service-card-full.all', types: [MeetingTypeEnum.not_defined] },
  ];
  public selectUserServicesFilter: UserServiceCategory;
  public userServiceCardFullInModal: boolean;
  public openedUserService: UserService;
  public surfaceBlockDisplay = true;
  public routes = EContentPanelRoute;

  private originalUrl: string;
  private subjectId: string;
  private _selectedSubjectId: string;

  public ngOnInit(): void {
    this.init();
  }

  public onCancel() {
    this.editService = null;
  }

  public onChange() {
    this.editService = null;
    this.loadUserServices();
  }

  private init(): void {
    // this.loading = true;
    this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.router.url.split('/').pop();
    this.prepareCardByProfile();
    this.loading = false;
    this.getSubjectIdFromUrl$
      .pipe(tap(() => this.initTeachingSubject()))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected prepareCardByProfile() {
    if (!this._user || !this._profile || !this.subjects.length) {
      return;
    }

    this.accountRoute = AccountService.accountRoute(this._user).join('/');
    this.teachingSubjectsList = (this._profile.teachingSubjects || [])
      .filter((s) => s.subjectId > 0)
      .map((subject, index) => {
        const _subject = this.subjects.find((s) => s.id === +subject.subjectId);
        return {
          value: index,
          title: (_subject ? _subject.title : null) || '---',
          route: AccountService.accountRoute(this._user, ['teaching', subject.subjectId.toString()]),
          native: findSubjectById(this.subjects, subject)?.category_id === SubjectCategoryEnum.native_language,
          subject: {
            subject_id: subject.subjectId,
            subject: null,
            subject_other_name: null,
            subject_other_id: null,
            description: subject.description,
            video_url: subject.videoUrl,
            video_id: subject.videoId,
            video: null,
            video_mode: subject.videoMode,
          },
        };
      });
    this.teachingSubjectsList = uniqBy(this.teachingSubjectsList, (e) => e.subject.subject_id);

    const _filter: Partial<FindUserServicesFilter> = {
      ...this.services.filter,
      author_id: this._user.id,
    };

    this.teachingSubjectsList.forEach((el) => {
      _filter.subject_id = el.subject.subject_id;
    });

    this.initTeachingSubject();
    this.isMe = this._me && this._user && this._me?.id === this._user.id;
    this.isGuest = this._me && this._me?.status === UserStatusEnum.guest;
  }

  protected initTeachingSubject() {
    if (this.subjectId && this.teachingSubjectsList.length) {
      const item = this.teachingSubjectsList.find((s) => s.subject.subject_id === +this.subjectId);
      this.teachingSubject = item ? item.subject : null;

      this.services.pagination.page = 1;
      this.services.items = [];
      this.loadUserServices();
    } else {
      this.services.loaded = true;
    }
  }

  private updateView() {
    if (!this.isDestroyed) {
      this.cdr.detectChanges();
    }
  }

  protected loadUserServices(mode: 'init' | 'loadNext' | 'filter' = 'init') {
    if (!this._user || !this.teachingSubject || this.services.loading) {
      return;
    }

    this.services.loading = true;
    this.updateView();

    const _filter: BffGetUserServicesEndpoint.Body = {
      page: this.services.pagination.page,
      pageSize: this.PAGE_SIZE,
      authorId: this._user.id,
    };

    _filter.subjectId = [Number(this.teachingSubject.subject_id)];
    if (this.selectUserServicesFilter && this.selectUserServicesFilter.id !== -1) {
      _filter.categoryId = this.selectUserServicesFilter.id;
    }
    _filter.subjectId = [Number(this.teachingSubject.subject_id)];

    this.userServicesV2Service
      .defaultGetList({
        strategy: 'profile',
        filter: {
          author_id: _filter.authorId,
          subject_id: _filter.subjectId,
          category_id: _filter.categoryId,
        },
        pagination: {
          page: _filter.page,
          pageSize: _filter.pageSize,
        },
      })
      .pipe(
        catchError((error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.services.loading = false;
          this.loaded.emit(true);
          this.updateView();
          return of(null);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response) => {
          if (mode !== 'filter') {
            this.services.items = this.filterUserServices([...this.services.items, ...response?.list]);
          } else {
            this.services.items = this.filterUserServices(response?.list);
          }
          this.services.pagination = response?.pagination;

          this.services.loading = false;
          this.services.loaded = true;
          this.activeServiceIndex = null;
          this.creteUserServicesFilterList();
          this.updateView();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.services.loading = false;
          this.loaded.emit(true);
          this.updateView();
        },
      });
  }

  public trackUserServiceByFn(userService: UserService): string {
    return userService.id;
  }

  public openCheckoutModal(index: number) {
    const onAuthComplete = () => {
      this.activeServiceIndex = index;
      this.checkoutService = this.services.items[index];
      this.dialogsService.openUserServiceCheckoutWizard({
        userServiceId: this.checkoutService.id,
      });
      this.detectChanges();
    };

    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public onIinViewportChange(e) {
    this.surfaceBlockDisplay = e;
    this.detectChanges();
  }

  public onSelectOtherUserServicesFilter(category: UserServiceCategory) {
    this.selectUserServicesFilter = category;
    this.loadUserServices('filter');
  }

  public filterUserServices(userServices: UserService[]): UserService[] {
    if (!this.selectUserServicesFilter || this.selectUserServicesFilter?.id === -1) {
      return userServices;
    }
    return userServices.filter((_us) => _us.category_id === this.selectUserServicesFilter.id);
  }

  public hasFreeHours(): boolean {
    return AccountService.hasSegment(this.user, UserSegmentEnum.has_free_hours_30days);
  }

  public onToggleUserServiceInModal(userService: UserService = null) {
    this.openedUserService = userService;
    this.userServiceCardFullInModal = !this.userServiceCardFullInModal;
    if (!this.userServiceCardFullInModal) {
      this.isCloseAllModals.emit(true);
    }
    this.setAndResetUrl();
    this.detectChanges();
  }

  private setAndResetUrl() {
    const url = this.helpersBffService.userServiceRouteUniversal(this.openedUserService);
    const currentUrl = this.location.path(false);
    if (this.userServiceCardFullInModal) {
      if (currentUrl !== url) {
        if (!this.originalUrl) {
          this.originalUrl = currentUrl;
        }
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  private creteUserServicesFilterList() {
    if (this.userServiceCategoryTabs.length > 1) {
      return;
    }
    this.userServiceCategoryTabs = [
      { id: -1, code: 'all', title: 'user-service-card-full.all', types: [MeetingTypeEnum.not_defined] },
    ];
    const tabs = (this.usCategories || [])?.filter(
      (_category) => !!this.services?.items.find((_us) => +_us.category_id === +_category.id),
    );
    this.userServiceCategoryTabs.push(...tabs);
    this.selectUserServicesFilter = this.userServiceCategoryTabs[0];
    this.loaded.emit(true);
  }

  private get getSubjectIdFromUrl$(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.parseSubjectIdFromUrl(event.url);
        return this.subjectId;
      }),
    );
  }

  private parseSubjectIdFromUrl(url: string): string {
    return this.router.url.split('/').pop();
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible && !this.loading) {
      if (this.services.pagination.page < this.services.pagination.totalPages) {
        this.loadNextPage();
      }
    }
  }

  public loadNextPage() {
    if (this.services.loading) {
      return;
    }
    this.services.pagination.page += 1;
    this.loadUserServices();
  }

  public ngOnDestroy(): void {
    this.isDestroyed = true;
    super.ngOnDestroy();
  }
}
